import { admin2 } from './instance'

const baseUrl = (shopId) => `/${shopId}/miezoAccessControlPassword`

/**
 * 新增合約密碼 admin.miezoAccessControlPassword.create
 * @param {Object} params - 新增參數
 * @param {string} params.shopId - 商店ID
 * @param {string} [params.name] - 名稱
 * @param {string[]} params.MiezoAccessControlIds - 持有門禁ID列表
 * @param {string} params.effectiveStartAt - 有效開始時間
 * @param {string} params.effectiveEndAt - 有效結束時間
 * @param {string[]} [params.memberIds] - 綁定會員ID列表
 * @returns {Promise<Object>} 合約密碼物件
 */
export const CreateMiezoAccessControlPassword = async ({
  shopId,
  name,
  MiezoAccessControlIds,
  effectiveStartAt,
  effectiveEndAt,
  memberIds,
}) => {
  return await admin2({
    method: 'POST',
    url: baseUrl(shopId),
    data: {
      name,
      MiezoAccessControlIds,
      effectiveStartAt,
      effectiveEndAt,
      memberIds,
    },
  })
}

/**
 * 更新合約密碼 admin.miezoAccessControlPassword.update
 * @param {Object} params - 更新參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.id - 合約密碼ID
 * @param {string} [params.name] - 名稱
 * @param {string[]} [params.memberIds] - 綁定會員ID列表
 * @returns {Promise<Object>} 合約密碼物件
 */
export const UpdateMiezoAccessControlPassword = async ({
  shopId,
  id,
  name,
  memberIds,
}) => {
  return await admin2({
    method: 'PUT',
    url: `${baseUrl(shopId)}/${id}`,
    data: {
      name,
      memberIds,
    },
  })
}

/**
 * 刪除合約密碼 admin.miezoAccessControlPassword.delete
 * @param {Object} params - 刪除參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.id - 合約密碼ID
 * @returns {Promise<Object>} { success: boolean, message: string }
 */
export const DeleteMiezoAccessControlPassword = async ({ shopId, id }) => {
  return await admin2({
    method: 'DELETE',
    url: `${baseUrl(shopId)}/${id}`,
  })
}

/**
 * 查詢合約密碼列表 admin.miezoAccessControlPassword.find
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {number} [params.start=0] - 查詢起始
 * @param {number} [params.limit=20] - 查詢筆數（最大100）
 * @param {string} [params.memberId] - 會員ID
 * @param {string} [params.createSource] - 建立來源 (admin|order)
 * @param {string} [params.effectiveStatus] - 有效狀態，支援逗號分隔 (notEffective,effective,expired)
 * @param {string} [params.MiezoAccessControlId] - 門禁設備ID
 * @param {string} [params.name] - 名稱（模糊搜尋）
 * @returns {Promise<Array>} 合約密碼列表
 */
export const GetMiezoAccessControlPasswordList = async ({
  shopId,
  start,
  limit,
  memberId,
  createSource,
  effectiveStatus,
  MiezoAccessControlId,
  name,
}) => {
  return await admin2({
    method: 'GET',
    url: baseUrl(shopId),
    params: {
      start,
      limit,
      memberId,
      createSource,
      effectiveStatus,
      MiezoAccessControlId,
      name,
    },
  })
}

/**
 * 查詢單一合約密碼 admin.miezoAccessControlPassword.findOne
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.id - 合約密碼ID
 * @returns {Promise<Object>} 合約密碼物件
 */
export const FindMiezoAccessControlPassword = async ({ shopId, id }) => {
  return await admin2({
    method: 'GET',
    url: `${baseUrl(shopId)}/${id}`,
  })
}

/**
 * 統計合約密碼數量 admin.miezoAccessControlPassword.count
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} [params.memberId] - 會員ID
 * @param {string} [params.createSource] - 建立來源 (admin|order)
 * @param {string} [params.effectiveStatus] - 有效狀態，支援逗號分隔 (notEffective,effective,expired)
 * @param {string} [params.MiezoAccessControlId] - 門禁設備ID
 * @param {string} [params.name] - 名稱（模糊搜尋）
 * @returns {Promise<Object>} { count: number }
 */
export const GetMiezoAccessControlPasswordCount = async ({
  shopId,
  memberId,
  createSource,
  effectiveStatus,
  MiezoAccessControlId,
  name,
}) => {
  return await admin2({
    method: 'GET',
    url: `${baseUrl(shopId)}/count`,
    params: {
      memberId,
      createSource,
      effectiveStatus,
      MiezoAccessControlId,
      name,
    },
  })
}

/**
 * 重新產生密碼 admin.miezoAccessControlPassword.regenerate
 * @param {Object} params - 重新產生參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.id - 合約密碼ID
 * @returns {Promise<Object>} { id, password, updatedAt }
 */
export const RegenerateMiezoAccessControlPassword = async ({ shopId, id }) => {
  return await admin2({
    method: 'POST',
    url: `${baseUrl(shopId)}/${id}/regenerate`,
    data: {},
  })
}

/**
 * 產生密碼 QR Code admin.miezoAccessControlPassword.generateQRCode
 * @param {Object} params - 產生參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.id - 合約密碼ID
 * @returns {Promise<Object>} { qrCode: string } 純文字，需使用 QR Code 函式庫產生圖片
 */
export const GenerateMiezoAccessControlPasswordQRCode = async ({
  shopId,
  id,
}) => {
  return await admin2({
    method: 'POST',
    url: `${baseUrl(shopId)}/${id}/qrcode`,
    data: {},
  })
}
